import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  H2, 
  H3, 
  H4,
  InputGroup, 
  NumericInput, 
  HTMLSelect, 
  Button, 
  Tag,
  Intent,
  Dialog,
  DialogBody,
  DialogFooter,
  RadioGroup,
  Radio
} from '@blueprintjs/core';
import { saveActToHistory, testFirebaseConnection } from '../firebase/actService';
import PenaltyWidget from '../components/PenaltyWidget';
import DraggableRaceInput from '../components/DraggableRaceInput';
import { TOP_BRACKET_CUPS as CUPS, CUP_EMOJIS, pointsToPlace } from '../constants/cups';

const TEAMS = ['A', 'B', 'C', 'D'];
const STORAGE_KEY = 'ACT_COMPACT_SCORECARD';
const HISTORY_KEY = 'ACT_SCORECARD_HISTORY';
const MODE_STORAGE_KEY = 'ACT_MODE'; // Store the selected mode
const MODE_CONFIG = {
  '8': {
    playerKeys: ['A', 'B'],
    racesPerCup: 4,
    defaultAssignments: ['A', 'B', 'A', 'B'],
    modeLabel: '8/8',
    toggleHint: 'A/B'
  },
  '12': {
    playerKeys: ['A', 'B', 'C'],
    racesPerCup: 6,
    defaultAssignments: ['A', 'B', 'C', 'A', 'B', 'C'],
    modeLabel: '12/12',
    toggleHint: 'A/B/C'
  },
  '16': {
    playerKeys: ['A', 'B', 'C', 'D'],
    racesPerCup: 8,
    defaultAssignments: ['D', 'A', 'D', 'A', 'B', 'C', 'B', 'C'],
    modeLabel: '16/16',
    toggleHint: 'A/B/C/D'
  }
};

const getModeConfig = (mode) => MODE_CONFIG[mode] || MODE_CONFIG['8'];

const getDefaultRacerSlot = (mode, raceIndex) => {
  const { defaultAssignments } = getModeConfig(mode);
  return defaultAssignments[raceIndex] || defaultAssignments[raceIndex % defaultAssignments.length];
};

const createRaceAssignments = (mode) => {
  const { defaultAssignments } = getModeConfig(mode);
  const assignments = {};

  TEAMS.forEach(team => {
    assignments[team] = {};
    CUPS.forEach(cup => {
      assignments[team][cup] = [...defaultAssignments];
    });
  });

  return assignments;
};

const createInitialGameState = (mode) => {
  const { playerKeys, racesPerCup } = getModeConfig(mode);
  const initialState = {
    teams: {},
    points: {},
    date: new Date()
  };

  TEAMS.forEach(team => {
    initialState.teams[team] = {
      players: {},
      character: ''
    };

    playerKeys.forEach(player => {
      initialState.teams[team].players[player] = '';
    });
  });

  CUPS.forEach(cup => {
    initialState.points[cup] = {};
    TEAMS.forEach(team => {
      initialState.points[cup][team] = new Array(racesPerCup).fill(0);
    });
  });

  return initialState;
};

const CHARACTER_OPTIONS = [
  { label: 'Select Character', value: '' },
  { label: 'Daisy', value: 'Daisy' },
  { label: 'Peach', value: 'Peach' },
  { label: 'Luigi', value: 'Luigi' },
  { label: 'Mario', value: 'Mario' },
  { label: 'Funky Kong', value: 'Funky Kong' },
  { label: 'Mii', value: 'Mii' },
  { label: 'Birdo', value: 'Birdo' },
  { label: 'Diddy Kong', value: 'Diddy Kong' },
  { label: 'Other', value: 'Other' }
];

function Scorecard() {
  const navigate = useNavigate();
  const [isTieBreakModalOpen, setIsTieBreakModalOpen] = useState(false);
  const [tieInfo, setTieInfo] = useState(null); // { position: 'first'|'second', teams: [...] }
  const [selectedWinner, setSelectedWinner] = useState('');
  const [penalties, setPenalties] = useState({});
  
  // ACT mode state (8-person vs 12-person)
  const [actMode, setActMode] = useState(() => {
    const savedMode = localStorage.getItem(MODE_STORAGE_KEY);
    return MODE_CONFIG[savedMode] ? savedMode : '8'; // Default to 8-person mode
  });
  const modeConfig = getModeConfig(actMode);
  
  // Dynamic player keys based on mode
  const PLAYER_KEYS = modeConfig.playerKeys;
  const RACES_PER_CUP = modeConfig.racesPerCup;
  
  // Password protection state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  
  const SCORECARD_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;
  const AUTH_STORAGE_KEY = 'ACT_SCORECARD_AUTH';
  const AUTH_DURATION = 20 * 60 * 1000; // 20 minutes in milliseconds
  
  // Check for existing valid authentication on component mount
  useEffect(() => {
    const checkAuthStatus = () => {
      const authData = localStorage.getItem(AUTH_STORAGE_KEY);
      if (authData) {
        try {
          const { timestamp } = JSON.parse(authData);
          const now = Date.now();
          const timeSinceAuth = now - timestamp;
          
          if (timeSinceAuth < AUTH_DURATION) {
            setIsAuthenticated(true);
            return;
          } else {
            // Auth expired, remove from storage
            localStorage.removeItem(AUTH_STORAGE_KEY);
          }
        } catch (error) {
          // Invalid auth data, remove it
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      }
    };
    
    checkAuthStatus();
  }, []);
  
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === SCORECARD_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError(false);
      setPasswordInput('');
      
      // Store authentication timestamp in localStorage
      const authData = {
        timestamp: Date.now()
      };
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
    } else {
      setPasswordError(true);
      setPasswordInput('');
      // Clear error after 3 seconds
      setTimeout(() => setPasswordError(false), 3000);
    }
  };
  
  // Race assignments state - tracks which player races each slot
  const [raceAssignments, setRaceAssignments] = useState(() => createRaceAssignments(actMode));
  const [gameState, setGameState] = useState(() => createInitialGameState(actMode));

  // Remove review modal state since we're replacing with simple finish
  const inputRefs = useRef([]);

  // Save to localStorage with debouncing
  const saveTimeoutRef = useRef(null);
  
  const debouncedSave = useCallback(() => {
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(gameState));
    }, 500);
  }, [gameState]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsedState = JSON.parse(saved);
        // Check if saved state matches current mode
        const savedRaceCount = parsedState.points?.Mushroom?.A?.length || 4;
        const expectedRaceCount = RACES_PER_CUP;
        
        if (savedRaceCount === expectedRaceCount) {
          // Mode matches, load the state
          setGameState(prevState => ({
            teams: { ...prevState.teams, ...parsedState.teams },
            points: { ...prevState.points, ...parsedState.points },
            date: parsedState.date ? new Date(parsedState.date) : new Date()
          }));
        } else {
          // Mode doesn't match, clear saved state
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('Error loading saved state:', error);
      }
    }
  }, [RACES_PER_CUP]);

  // Save when gameState changes
  useEffect(() => {
    debouncedSave();
  }, [gameState]);

  // Helper functions

  const cupSubtotal = (cup, team) => {
    return gameState.points[cup][team].reduce((sum, points) => {
      if (points === 'X') return sum; // Skip void races
      return sum + (parseInt(points) || 0);
    }, 0);
  };

  const grandTotal = (team) => {
    const baseTotal = CUPS.reduce((total, cup) => total + cupSubtotal(cup, team), 0);
    const teamPenalty = penalties[team] || 0;
    return baseTotal + teamPenalty;
  };

  const isRaceSumValid = (cup, raceIndex) => {
    // Check if race index is valid for current mode
    if (raceIndex >= RACES_PER_CUP) return true;
    
    const sum = TEAMS.reduce((total, team) => {
      const points = gameState.points[cup]?.[team]?.[raceIndex];
      if (points === undefined || points === 'X') return total; // Skip void races or undefined
      return total + (parseInt(points) || 0);
    }, 0);
    
    // Check if any team has void, if so, sum should be 0-5
    const hasVoid = TEAMS.some(team => gameState.points[cup]?.[team]?.[raceIndex] === 'X');
    if (hasVoid) {
      return sum >= 0 && sum <= 5; // Allow 0-5 points if someone voided
    }
    return sum === 6; // Normal race should sum to 6
  };

  const areAllPointsValid = () => {
    for (const cup of CUPS) {
      for (const team of TEAMS) {
        for (let i = 0; i < RACES_PER_CUP; i++) {
          const points = gameState.points[cup]?.[team]?.[i];
          if (points === undefined || points === 'X') continue; // Void races or undefined are valid
          const numPoints = parseInt(points);
          if (isNaN(numPoints) || numPoints < 0 || numPoints > 3) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const areAllRaceSumsValid = () => {
    for (const cup of CUPS) {
      for (let race = 0; race < RACES_PER_CUP; race++) {
        if (!isRaceSumValid(cup, race)) {
          return false;
        }
      }
    }
    return true;
  };


  // Event handlers
  const handlePlayerNameChange = (team, player, value) => {
    setGameState(prev => ({
      ...prev,
      teams: {
        ...prev.teams,
        [team]: {
          ...prev.teams[team],
          players: {
            ...prev.teams[team].players,
            [player]: value
          }
        }
      }
    }));
  };

  const handleCharacterChange = (team, character) => {
    setGameState(prev => ({
      ...prev,
      teams: {
        ...prev.teams,
        [team]: {
          ...prev.teams[team],
          character
        }
      }
    }));
  };

  const handleDateChange = (date) => {
    setGameState(prev => ({
      ...prev,
      date: date || new Date()
    }));
  };

  const handlePointChange = (cup, team, raceIndex, value) => {
    // Allow 'X' for void/DNF races, otherwise clamp to 0-3
    let finalValue;
    if (value === 'X' || value === 'x') {
      finalValue = 'X';
    } else {
      const numValue = parseInt(value);
      if (isNaN(numValue)) {
        finalValue = 0;
      } else {
        finalValue = Math.max(0, Math.min(3, numValue));
      }
    }
    
    setGameState(prev => ({
      ...prev,
      points: {
        ...prev.points,
        [cup]: {
          ...prev.points[cup],
          [team]: prev.points[cup][team].map((point, index) => 
            index === raceIndex ? finalValue : point
          )
        }
      }
    }));
  };

  const advanceToNextInput = (currentIndex) => {
    if (currentIndex !== -1 && currentIndex < inputRefs.current.length - 1) {
      const nextInput = inputRefs.current[currentIndex + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }
  };

  const handleKeyDown = (e, currentIndex) => {
    // Allow: backspace, delete, tab, escape, enter, numbers 0-3, and X key
    if ([8, 9, 27, 13, 46, 48, 49, 50, 51, 88].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    // Ensure that it is a number, X, or allowed key and stop other keypresses
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 51)) && (e.keyCode < 96 || e.keyCode > 99) && e.keyCode !== 88) {
      e.preventDefault();
    }
  };

  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      localStorage.removeItem(STORAGE_KEY);
      setPenalties({});

      setGameState(createInitialGameState(actMode));
      setRaceAssignments(createRaceAssignments(actMode));
    }
  };

  // Use imported unified placement utility

  // Check for ties in final standings
  const checkForTies = () => {
    const teamTotals = TEAMS.map(team => ({
      team,
      total: grandTotal(team),
      displayName: getTeamDisplayName(team)
    })).sort((a, b) => b.total - a.total);

    // Find the highest score and all teams that have it
    const maxScore = teamTotals[0].total;
    const tiedForFirst = teamTotals.filter(t => t.total === maxScore);
    
    // If there's a first place tie, return it
    if (tiedForFirst.length > 1) {
      return { position: 'first', teams: tiedForFirst };
    }
    
    // Check for second place ties (only if exactly 2 teams are tied)
    const secondScore = teamTotals[1].total;
    const tiedForSecond = teamTotals.filter(t => t.total === secondScore);
    
    if (tiedForSecond.length === 2) {
      return { position: 'second', teams: tiedForSecond };
    }
    
    return null;
  };

  const handleTieBreakSubmit = () => {
    if (!selectedWinner) {
      alert('Please select a winner to break the tie.');
      return;
    }
    
    setIsTieBreakModalOpen(false);
    proceedWithFinish();
  };

  const closeTieBreakModal = () => {
    setIsTieBreakModalOpen(false);
    setSelectedWinner('');
    setTieInfo(null);
  };


  // Check for ties first, then proceed with finish
  const handleFinish = () => {
    const ties = checkForTies();
    
    if (ties) {
      // There's a tie, show tie-breaking modal
      setTieInfo(ties);
      setSelectedWinner('');
      setIsTieBreakModalOpen(true);
    } else {
      // No ties, proceed directly
      proceedWithFinish();
    }
  };

  // Updated finish function that saves to Firebase and redirects
  const proceedWithFinish = async () => {
    try {
      console.log('🏁 Finish ACT clicked - testing Firebase connection first...');
      
      // Test Firebase connection first
      const isConnected = await testFirebaseConnection();
      if (!isConnected) {
        throw new Error('Firebase connection failed');
      }
      
      console.log('🏁 Firebase connected, now saving scorecard...');
      await saveToFirebase();
      
      // Clear the scorecard after successful save
      clearScorecard();
      
      // Show success message and redirect
      alert('ACT scorecard saved to Firebase!');
      navigate('/history');
    } catch (error) {
      console.error('❌ Error saving to Firebase:', error);
      alert(`Error saving ACT scorecard: ${error.message}. Check console for details.`);
    }
  };

  const clearScorecard = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPenalties({});

    setGameState(createInitialGameState(actMode));
    setRaceAssignments(createRaceAssignments(actMode));
  };

  // Handle mode toggle
  const handleModeToggle = (newMode) => {
    if (newMode === actMode) return;
    
    const hasData = CUPS.some(cup => 
      TEAMS.some(team => 
        gameState.points[cup]?.[team]?.some(p => p !== 0 && p !== '0')
      )
    );
    
    if (hasData && !window.confirm('Switching modes will clear all current data. Continue?')) {
      return;
    }
    
    // Save mode preference
    localStorage.setItem(MODE_STORAGE_KEY, newMode);
    setActMode(newMode);
    
    // Clear and reinitialize for new mode
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload(); // Simplest way to reinitialize everything
  };
  
  const saveToFirebase = async () => {
    // Convert compact format to regular format for history compatibility
    const races = {};
    
    // Initialize races structure based on mode
    CUPS.forEach(cup => {
      races[cup] = {};
      for (let i = 1; i <= RACES_PER_CUP; i++) {
        races[cup][i] = {};
        TEAMS.forEach(team => {
          races[cup][i][team] = {};
        });
      }
    });
    
    CUPS.forEach(cup => {
      for (let raceIndex = 0; raceIndex < RACES_PER_CUP; raceIndex++) {
        const raceNumber = raceIndex + 1;
        TEAMS.forEach(team => {
          const points = gameState.points[cup][team][raceIndex];
          const racerSlot = raceAssignments[team]?.[cup]?.[raceIndex] ||
            getDefaultRacerSlot(actMode, raceIndex);
          const racerName = gameState.teams[team].players[racerSlot] || racerSlot;
          const place = pointsToPlace(points === 'X' ? 'X' : (points || 0));
          
          races[cup][raceNumber][team] = {
            racer: racerName,
            place: place
          };
        });
      }
    });
    
    const entry = {
      date: gameState.date.toISOString(),
      teams: JSON.parse(JSON.stringify(gameState.teams)),
      races: races,
      totals: {},
      mode: actMode // Store the mode this ACT was played in
    };
    
    TEAMS.forEach(team => {
      entry.totals[team] = grandTotal(team);
    });

    // Apply tie-breaking adjustment if there was a tie
    if (selectedWinner && tieInfo && tieInfo.teams.length > 1) {
      const tiedTeamIds = tieInfo.teams.map(t => t.team);
      
      if (tiedTeamIds.includes(selectedWinner)) {
        if (tieInfo.position === 'first') {
          // For first place ties, give winner a small boost
          const maxScore = Math.max(...Object.values(entry.totals));
          entry.totals[selectedWinner] = maxScore + 0.1;
        } else if (tieInfo.position === 'second') {
          // For second place ties, give winner a small boost but keep below first place
          const tieScore = tieInfo.teams[0].total;
          entry.totals[selectedWinner] = tieScore + 0.1;
        }
      }
    }
    
    // Save to Firebase
    await saveActToHistory(entry);
    
    // Also save to localStorage as backup (optional)
    const history = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    const entryWithId = { ...entry, id: Date.now().toString() };
    history.unshift(entryWithId);
    if (history.length > 50) {
      history.pop();
    }
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  };



  const getTeamDisplayName = (team) => {
    const players = gameState.teams[team]?.players || {};
    const playerNames = PLAYER_KEYS.map(key => players[key]).filter(name => name && name !== '');
    
    if (playerNames.length > 0) {
      if (PLAYER_KEYS.length > 2) {
        return PLAYER_KEYS.map(key => `${key}: ${players[key] || 'Player ' + key}`).join(', ');
      } else {
        const playerA = players.A || 'Player A';
        const playerB = players.B || 'Player B';
        if (playerA !== 'Player A' || playerB !== 'Player B') {
          return `A: ${playerA} & B: ${playerB}`;
        }
      }
    }
    return `Team ${team}`;
  };

  const getTeamHeaderDisplayName = (team) => {
    const players = gameState.teams[team]?.players || {};
    const hasPlayers = PLAYER_KEYS.some(key => players[key] && players[key] !== '');
    
    if (hasPlayers) {
      return (
        <span style={{ fontSize: PLAYER_KEYS.length >= 4 ? '0.6rem' : PLAYER_KEYS.length === 3 ? '0.65rem' : '0.7rem' }}>
          {PLAYER_KEYS.map(key => (
            <React.Fragment key={key}>
              {key}: {players[key] || 'Player ' + key}
              {key !== PLAYER_KEYS[PLAYER_KEYS.length - 1] && <br/>}
            </React.Fragment>
          ))}
        </span>
      );
    }
    return `Team ${team}`;
  };

  const getGrandTotalDisplay = (team) => {
    const total = grandTotal(team);
    const players = gameState.teams[team]?.players || {};
    const hasPlayers = PLAYER_KEYS.some(key => players[key] && players[key] !== '');
    
    if (hasPlayers) {
      return (
        <>
          {PLAYER_KEYS.map(key => (
            <React.Fragment key={key}>
              <strong>{key}:</strong> {players[key] || 'Player ' + key}<br/>
            </React.Fragment>
          ))}
          <strong>{total} pts</strong>
        </>
      );
    }
    return (
      <>
        <strong>Team {team}</strong><br/>{total} pts
      </>
    );
  };

  // Create input refs
  useEffect(() => {
    inputRefs.current = [];
  }, []);


  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Main Content - disabled when not authenticated */}
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        pointerEvents: isAuthenticated ? 'auto' : 'none',
        userSelect: isAuthenticated ? 'auto' : 'none',
        opacity: isAuthenticated ? 1 : 0.6
      }}>
        {/* Team Setup Section */}
      <section style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <H2 style={{ margin: 0 }}>Team Setup</H2>
          <Tag intent={Intent.PRIMARY}>Mario Kart ACT Tournament</Tag>
          
          {/* Mode Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '4px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px' }}>
            <Button
              small
              intent={actMode === '8' ? Intent.PRIMARY : Intent.NONE}
              onClick={() => handleModeToggle('8')}
              style={{ minWidth: '60px' }}
            >
              8-Man
            </Button>
            <Button
              small
              intent={actMode === '12' ? Intent.PRIMARY : Intent.NONE}
              onClick={() => handleModeToggle('12')}
              style={{ minWidth: '60px' }}
            >
              12-Man
            </Button>
            <Button
              small
              intent={actMode === '16' ? Intent.PRIMARY : Intent.NONE}
              onClick={() => handleModeToggle('16')}
              style={{ minWidth: '60px' }}
            >
              16-Man
            </Button>
          </div>
          
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>ACT Date:</span>
            <input
              type="date"
              value={gameState.date.toISOString().split('T')[0]}
              onChange={(e) => handleDateChange(new Date(e.target.value))}
              style={{
                padding: '6px 12px',
                borderRadius: '3px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                width: '150px'
              }}
            />
          </div>
        </div>
        <div className="team-cards-grid">
          {TEAMS.map(team => (
            <Card key={team} style={{ 
              padding: '1.25rem',
              background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))',
              border: '1px solid var(--border)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow)';
            }}
            >
              <H3 style={{ 
                fontSize: '1.1rem', 
                marginBottom: '1rem',
                color: 'var(--accent-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                🏎️ {getTeamDisplayName(team)}
              </H3>
              <div style={{ marginBottom: '1rem' }}>
                {PLAYER_KEYS.map(player => (
                  <div key={player} className="player-row">
                    <label>{player}:</label>
                    <InputGroup
                      placeholder={`Player ${player}`}
                      value={gameState.teams[team].players[player]}
                      onChange={(e) => handlePlayerNameChange(team, player, e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <span className="race-counter">{modeConfig.modeLabel}</span>
                  </div>
                ))}
              </div>
              <HTMLSelect
                value={gameState.teams[team].character}
                onChange={(e) => handleCharacterChange(team, e.target.value)}
                options={CHARACTER_OPTIONS}
                fill
              />
            </Card>
          ))}
        </div>
      </section>

      <div className="content-wrapper">
        {/* Races Section */}
        <section style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <H2 style={{ margin: 0 }}>Race Results</H2>
            <div style={{ display: 'flex', gap: '2rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ backgroundColor: '#ffeb3b66', padding: '2px 4px', borderRadius: '3px', fontWeight: 600, color: 'var(--accent-primary)' }}>A</span> 
                Player A
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>B</span> 
                Player B
              </span>
              {PLAYER_KEYS.includes('C') && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ backgroundColor: '#87ceeb66', padding: '2px 4px', borderRadius: '3px', fontWeight: 600, color: 'var(--accent-primary)' }}>C</span> 
                  Player C
                </span>
              )}
              {PLAYER_KEYS.includes('D') && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ backgroundColor: '#86efac66', padding: '2px 4px', borderRadius: '3px', fontWeight: 600, color: 'var(--accent-primary)' }}>D</span>
                  Player D
                </span>
              )}
              <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                (press H to toggle {modeConfig.toggleHint})
              </span>
            </div>
          </div>
          
          <div className="cups-container" data-mode={actMode}>
            {/* Header Row */}
            <div className="cup-header-row">
              <div className="cup-label"></div>
              {TEAMS.map(team => (
                <div key={team} className="team-header" data-team={team}>
                  {getTeamHeaderDisplayName(team)}
                </div>
              ))}
            </div>

            {/* Cup Rows */}
            {CUPS.map(cup => (
              <div key={cup} className="cup-row" data-cup={cup}>
                <div className="cup-label">
                  {CUP_EMOJIS[cup]} {cup}
                </div>
                {TEAMS.map(team => (
                  <div key={team} className="team-scores" data-team={team} data-races={RACES_PER_CUP}>
                    <div className="team-score-inputs">
                      {Array.from({ length: RACES_PER_CUP }, (_, raceIndex) => {
                        const inputIndex = inputRefs.current.length;
                        return (
                          <DraggableRaceInput
                            key={raceIndex}
                            className="bp5-input point-input"
                            style={{
                              width: '40px',
                              textAlign: 'center',
                              fontWeight: '600'
                            }}
                            value={gameState.points[cup][team][raceIndex]}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              if (value === 'X' || value === '' || (value >= '0' && value <= '3')) {
                                handlePointChange(cup, team, raceIndex, value);
                                if (value !== '' && (value === 'X' || (parseInt(value) >= 0 && parseInt(value) <= 3))) {
                                  advanceToNextInput(inputIndex);
                                }
                              }
                            }}
                            onKeyDown={(e) => handleKeyDown(e, inputIndex)}
                            placeholder="0"
                            inputRef={(el) => {
                              if (el) inputRefs.current[inputIndex] = el;
                            }}
                            cup={cup}
                            team={team}
                            raceIndex={raceIndex}
                            assignments={raceAssignments}
                            onAssignmentsChange={setRaceAssignments}
                            actMode={actMode}
                          />
                        );
                      })}
                    </div>
                    <span className="cup-total">= {cupSubtotal(cup, team)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* Summary Sidebar */}
        <aside className="summary-sidebar" style={{
          background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))',
          border: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <H2 style={{ margin: 0 }}>📊 Summary</H2>
          </div>

          {/* Penalty Widget */}
          <PenaltyWidget
            teams={TEAMS}
            penalties={penalties}
            onPenaltyChange={setPenalties}
            getTeamDisplayName={getTeamDisplayName}
          />
          <div>
            <H3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 1rem 0' }}>
              🏆 Grand Totals
            </H3>
            <div className="grand-total-scores">
              {TEAMS.map(team => (
                <div key={team} className="grand-total">
                  {getGrandTotalDisplay(team)}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <H3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 1rem 0' }}>
              🏁 Race Count
            </H3>
            <div className="player-count-grid">
              {TEAMS.map(team => {
                const racesPerPlayer = 8;
                return (
                  <div key={team} className="team-counts">
                    <H4>Team {team}</H4>
                    {PLAYER_KEYS.map(player => (
                      <div key={player} className="player-count">
                        {player}: {racesPerPlayer}/{racesPerPlayer}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <H3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 1rem 0' }}>
              ✅ Validation
            </H3>
            <div>
              <div className={`check-item ${areAllPointsValid() ? 'valid' : 'invalid'}`}>
                <span className="check-icon">{areAllPointsValid() ? '✅' : '❌'}</span>
                All points valid (0-3)
              </div>
              <div className={`check-item ${areAllRaceSumsValid() ? 'valid' : 'invalid'}`}>
                <span className="check-icon">{areAllRaceSumsValid() ? '✅' : '❌'}</span>
                Each race sums to 6 points
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <footer style={{ 
        marginTop: 'auto', 
        padding: '1.5rem', 
        textAlign: 'center', 
        borderTop: '2px solid var(--border)',
        background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))'
      }}>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', alignItems: 'center' }}>
          <Button 
            intent={Intent.WARNING} 
            icon="trash"
            onClick={handleClearDraft}
          >
            Clear Draft
          </Button>
          <Button 
            intent={Intent.SUCCESS} 
            icon="endorsed"
            onClick={handleFinish}
            large
            style={{ minWidth: '140px' }}
          >
            🏁 Finish ACT
          </Button>
        </div>
      </footer>

      {/* Tie-Breaking Modal */}
      <Dialog
        isOpen={isTieBreakModalOpen}
        onClose={closeTieBreakModal}
        title={tieInfo?.position === 'first' ? "🏆 First Place Tie-Breaking" : "🥈 Second Place Tie-Breaking"}
        style={{ width: '500px' }}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
      >
        <DialogBody>
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
              {tieInfo?.position === 'first' 
                ? "There is a tie for first place! Please select the winner to break the tie."
                : "There is a tie for second place! Please select which team should get 2nd place."
              }
            </p>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              {tieInfo?.position === 'first'
                ? "The selected winner will receive 1st place, and the other tied team(s) will receive 2nd place."
                : "The selected team will receive 2nd place, and the other team will receive 3rd place."
              }
            </p>
            
            <H3 style={{ marginBottom: '1rem' }}>Tied Teams:</H3>
            <div style={{ marginBottom: '1.5rem' }}>
              {tieInfo?.teams.map(team => (
                <div key={team.team} style={{
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: 600 }}>{team.displayName}</span>
                  <span style={{ color: 'var(--accent-primary)', fontWeight: 700 }}>
                    {team.total} pts
                  </span>
                </div>
              ))}
            </div>

            <H3 style={{ marginBottom: '1rem' }}>
              {tieInfo?.position === 'first' ? "Select Winner:" : "Select 2nd Place:"}
            </H3>
            <RadioGroup
              onChange={(e) => setSelectedWinner(e.currentTarget.value)}
              selectedValue={selectedWinner}
            >
              {tieInfo?.teams.map(team => (
                <Radio
                  key={team.team}
                  label={team.displayName}
                  value={team.team}
                  style={{ marginBottom: '0.5rem' }}
                />
              ))}
            </RadioGroup>
          </div>
        </DialogBody>
        <DialogFooter
          actions={
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button onClick={closeTieBreakModal}>
                Cancel
              </Button>
              <Button
                intent={Intent.SUCCESS}
                onClick={handleTieBreakSubmit}
                disabled={!selectedWinner}
              >
                🏁 Finish ACT
              </Button>
            </div>
          }
        />
      </Dialog>
      </div>

      {/* Password Protection Overlay */}
      {!isAuthenticated && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          pointerEvents: 'all'
        }}>
          <Card style={{ 
            padding: '2rem', 
            maxWidth: '400px', 
            width: '90%',
            textAlign: 'center',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <H2 style={{ margin: '0 0 1.5rem 0', color: 'var(--accent-primary)' }}>
              🔒 Scorecard Access
            </H2>
            
            <p style={{ 
              margin: '0 0 1.5rem 0', 
              color: 'var(--text-secondary)',
              fontSize: '0.9rem'
            }}>
              Enter the password to access the tournament scorecard
            </p>
            
            <form onSubmit={handlePasswordSubmit}>
              <InputGroup
                type="password"
                placeholder="Enter password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                intent={passwordError ? Intent.DANGER : Intent.NONE}
                style={{ marginBottom: '1rem' }}
                autoFocus
              />
              
              {passwordError && (
                <div style={{ 
                  color: 'var(--error)', 
                  fontSize: '0.8rem', 
                  marginBottom: '1rem'
                }}>
                  ❌ Incorrect password. Please try again.
                </div>
              )}
              
              <Button 
                type="submit" 
                intent={Intent.PRIMARY} 
                large 
                fill
                disabled={!passwordInput.trim()}
              >
                Access Scorecard
              </Button>
            </form>
          </Card>
        </div>
      )}

    </div>
  );
}

export default Scorecard;
